import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai/index';
import Link from 'next/link';

import { FundingTypeEnum } from '@flipster/apis/v2/snapshot';
import { FundingTypeTextMapper } from '@flipster/apis/v2/snapshot/constants';
import { LeverageBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { FormattedMessage } from '@flipster/i18n';
import { TextButton } from '@flipster/react/design-system';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { Market, marketsBunja } from '@flipster/socket/r230522/market';
import { SpotMarket, spotMarketsBunja } from '@flipster/socket/r230522/spot-market';
import { css } from '@flipster/styled-system/css';
import { hstack, linkOverlay, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { alert } from '@flipster/ui/Alert';
import { FundingRatePrecision } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import { CarouselMapper, MarketCarouselType } from './constants';
import { SortedSymbolsCategory } from './types';
import { useSortedTopMarket } from './useSortedTopMarket';

interface Props {
  carouselId: MarketCarouselType;
  productType?: 'perpetual' | 'spot';
  onClickSeeAll?: (tab: SortedSymbolsCategory) => void;
}

export const SortedTopMarket = ({ carouselId, onClickSeeAll, productType }: Props) => {
  const { sortedTopMarkets } = useSortedTopMarket(productType);

  const { title, sub, alertProps, seeAll, category } = CarouselMapper[carouselId];

  return (
    <section
      className={css({
        minH: 322,
        py: 24,
        px: { base: 8, tablet: 16 },
        rounded: { base: 8, tablet: 16 },
        bgColor: 'background.default.level-1',
      })}
    >
      <header className={hstack({ justify: 'space-between', h: 26, px: 12, mb: 12 })}>
        <h4 className={css({ textStyle: 'Title/XS', color: 'content.default.level-4', flex: 1 })}>
          {title}
          {sub && <span className={css({ textStyle: 'Text/M/Regular', ml: 4 })}>{sub}</span>}
          {alertProps && (
            <button
              type='button'
              aria-label={`What is ${title}?`}
              onClick={() => alert.show(alertProps)}
              className={css({ buttonShape: 'inline-icon', ml: 2 })}
            >
              <InformationCircleIcon size={20} color={token('colors.content.default.level-1')} />
            </button>
          )}
        </h4>
        {seeAll && onClickSeeAll && (
          <TextButton
            color='passive'
            size='medium'
            label={<FormattedMessage id='Common.r250919.See_all' />}
            onClick={() => onClickSeeAll(category!)}
          />
        )}
      </header>
      <ul className={stack({ gap: 4 })}>
        {sortedTopMarkets[carouselId]?.map((symbol, index) => {
          return <Item symbol={symbol} carouselId={carouselId} index={index} key={`${title}_${symbol}`} />;
        })}
      </ul>
    </section>
  );
};

interface ItemProps {
  symbol: string;
  carouselId: MarketCarouselType;
  index: number;
}
const Item = ({ symbol, carouselId, index }: ItemProps) => {
  const { marketsAtom } = useBunja(marketsBunja);
  const { spotMarketsAtom } = useBunja(spotMarketsBunja);
  const market = useAtomValue(marketsAtom);
  const spotMarket = useAtomValue(spotMarketsAtom);
  const setOrderAttribution = useSetAtom(orderAttributionAtom);

  const { instrumentsKeyBySymbol } = useSnapshotSpec();

  const currentInstrument = instrumentsKeyBySymbol?.[symbol];
  const isSpot = Boolean(currentInstrument?.isSpot);
  const currentMarket: SpotMarket | Market | undefined = isSpot ? spotMarket?.[symbol] : market?.[symbol];

  return (
    <li
      className={css({
        pos: 'relative',
        px: 12,
        rounded: 8,
        _hover: {
          bgColor: 'background.interaction.hover',
        },
      })}
    >
      <div className={hstack({ h: 76, gap: 12 })}>
        <div className={css({ flex: 1 })}>
          <SymbolDisplay
            symbol={symbol}
            icon={<SymbolDisplay.Icon size={32} space={12} />}
            mainLabel={
              <SymbolDisplay.Label
                value='shortName'
                css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5', whiteSpace: 'nowrap' }}
              />
            }
            subLabel={
              currentInstrument && !isSpot && <LeverageBadge leverage={currentInstrument.maxLeverageHardLimit} />
            }
          />
        </div>
        <div
          className={stack({
            align: 'flex-end',
            gap: 2,
            flex: 1,
            textStyle: 'Text/L/Monospace/Regular',
            color: 'content.default.level-4',
          })}
        >
          {carouselId === 'volume' && (
            <>
              <Formatted.Number
                method='round-half-up'
                value={currentMarket?.midPrice}
                precision={currentInstrument?.pricePrecision}
                toFixed
              />
              <Formatted.ShortNumber
                value={currentMarket?.turnover24h}
                toFixed
                css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-2' }}
              />
            </>
          )}
          {!isSpot && carouselId === 'absFundingRates' && (
            <>
              <Formatted.Percent
                value={(currentMarket as Market | undefined)?.fundingRate}
                precision={FundingRatePrecision}
                isRate
                toFixed
                withColor
                withPlus
              />
              <span
                className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-2' })}
              >{`/${FundingTypeTextMapper[currentInstrument?.fundingType ?? FundingTypeEnum.ImmediateHour8]}`}</span>
            </>
          )}
          {carouselId !== 'volume' && carouselId !== 'absFundingRates' && (
            <>
              <Formatted.Number
                method='round-half-up'
                value={currentMarket?.midPrice}
                precision={currentInstrument?.pricePrecision}
                toFixed
              />
              <Formatted.Percent
                value={currentMarket?.priceChangePct24h}
                precision={2}
                toFixed
                withColor
                withArrow
                css={{ textStyle: 'Text/M/Monospace/Regular' }}
              />
            </>
          )}
        </div>
        <ChevronRightIcon size={18} color={token('colors.content.default.level-1')} />
      </div>

      {currentInstrument && (
        <Link
          href={currentInstrument.isSpot ? `/trade/spot/${symbol}` : `/trade/perpetual/${symbol}`}
          onClick={() => {
            setOrderAttribution(OrderAttributionMapper.market.categories[carouselId]);
            segment.track(MainEvent.CarouselProductClick, {
              carousel_type: carouselId,
              symbol,
              list_order: index,
              current_price: currentMarket?.midPrice || '0',
              percentage_diff: currentMarket?.priceChangePct24h || '0',
            });
          }}
          className={linkOverlay()}
        />
      )}
    </li>
  );
};
